import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import UserService from "../api/UserService";
import SubscriptionService from "../api/SubscriptionService";
import DocumentTypeService from "../api/DocumentTypeService";
import AuthService from "../api/AuthService";
import CategoryService from "../api/CategoryService";
import FieldDefinitionService from "../api/FieldDefinitionsService";

const fetchUserData = async (dispatch) => {
  await dispatch(fetchUserInfo());
  await Promise.all([
    dispatch(fetchSubscriptions()),
    dispatch(fetchCategories()),
    dispatch(fetchDocumentTypes()),
    dispatch(fetchFieldDefinitions())
  ]);
};

export const restoreSession = createAsyncThunk(
  'user/restoreSession', async (_, { dispatch }) => {
    try {
      const response = await UserService.getUserInfo();
      if (response.status === 200) {
        await fetchUserData(dispatch);
      } else {
        dispatch(userLogout());
      }
    } catch (error) {
      if (error.response && error.response.status !== 401 && error.response.status !== 403) {
        console.error(error);
      }
      dispatch(userLogout());
    }
  }
);

export const userLogin = createAsyncThunk(
  'user/login', async (_, { dispatch }) => {
    try {
      await fetchUserData(dispatch);
    } catch (error) {
      console.error(error);
      dispatch(userLogout());
    }
  }
);

export const fetchUserInfo = createAsyncThunk(
  'user/fetchUserInfo', async () => {
    const response = await UserService.getUserInfo();
    return response.data;
  });

export const fetchSubscriptions = createAsyncThunk(
  'user/fetchSubscriptions', async () => {
    return await SubscriptionService.getSubscriptions();
  });

export const fetchCategories = createAsyncThunk(
  'user/fetchCategories', async (_, { getState }) => {
    const state = getState();
    const user = state.user.user;
    if (user) {
      const response = await CategoryService.getAll();
      return response.data;
    }
  });

export const fetchDocumentTypes = createAsyncThunk(
  'user/fetchDocumentTypes', async (_, { getState }) => {
    const state = getState();
    const user = state.user.user;
    if (user) {
      const response = await DocumentTypeService.getAll();
      return response.data;
    }
  });

export const fetchFieldDefinitions = createAsyncThunk(
  'user/fetchFieldDefinitions', async (_, { getState }) => {
    const state = getState();
    const user = state.user.user;
    if (user) {
      const response = await FieldDefinitionService.getAll();
      return response.data;
    }
  });

const userSlice = createSlice({
  name: 'user',
  initialState: {
    user: null,
    isLoggedIn: false,
    isLoading: false,
    subscriptions: [],
    categories: [],
    documentTypes: [],
    fieldDefinitions: []
  },
  reducers: {
    userLogout: (state, action) => {
      try {
        if (action.payload !== "offline") {
          AuthService.logout();
          localStorage.removeItem("deviceId");
        }
      } finally {
        state.user = null;
        state.isLoggedIn = false;
        state.isLoading = false;
        state.subscriptions = [];
        state.storageUsage = null;
        state.categories = [];
        state.documentTypes = [];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.user = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptions.rejected, (state) => {
        state.subscriptions = [];
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.categories = [];
      })
      .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
        state.documentTypes = action.payload;
      })
      .addCase(fetchDocumentTypes.rejected, (state) => {
        state.documentTypes = [];
      })
      .addCase(fetchFieldDefinitions.fulfilled, (state, action) => {
        state.fieldDefinitions = action.payload;
      })
      .addCase(fetchFieldDefinitions.rejected, (state) => {
        state.fieldDefinitions = [];
      })
      .addCase(restoreSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(restoreSession.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.isLoading = false;
        state.isLoggedIn = false;
      });
  }
});

export const {
  userLogout
} = userSlice.actions;

export default userSlice.reducer;
